import { useEffect, useState, type ReactNode } from "react";
import { useOutletContext, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { Chip } from "@/components/Chip";
import { DateRangePicker } from "@/components/DateRangePicker";
import { EmptyState } from "@/components/EmptyState";
import { LoadError } from "@/components/LoadError";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { useApi } from "@/lib/api";
import { localDayEnd, localDayStart } from "@/lib/calendarDates";
import { formatDateTime } from "@/lib/formatDateTime";
import { formatDuration } from "@/lib/formatDuration";
import { POLL_INTERVAL_MS } from "@/lib/onlineStatus";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import type { LogsLayoutContext } from "./LogsLayout";
import {
  formatSize,
  type RecordedSessionSummary,
  type SessionRecordingSummary,
} from "./shared";

interface ExtraColumn<T> {
  label: string;
  render: (recording: T) => ReactNode;
}

export function SkeletonRows({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: 6 }, (_, row) => (
        <tr key={row} className="h-14 animate-pulse">
          <td className="py-2 pr-4">
            <div className="h-3 w-36 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 shrink-0 rounded-full bg-surface" />
              <div className="h-3 w-28 rounded bg-surface" />
            </div>
          </td>
          {Array.from({ length: columns }, (_, cell) => (
            <td key={cell} className="py-2 pr-4">
              <div className="h-3 w-20 rounded bg-surface" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function RecordingsPage<
  T extends RecordedSessionSummary = SessionRecordingSummary,
>({
  path = "session-recordings",
  connectionMethod,
  label,
  extraColumn,
  showSize = true,
  searchPlaceholder = "Search resource or user",
  emptyDescription,
  renderModal,
}: {
  path?: string;
  connectionMethod?: string;
  label: string;
  extraColumn?: ExtraColumn<T>;
  showSize?: boolean;
  searchPlaceholder?: string;
  emptyDescription?: string;
  renderModal: (recording: T, onClose: () => void) => ReactNode;
}) {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const { setSummary } = useOutletContext<LogsLayoutContext>();

  const [recordings, setRecordings] = useState<T[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [replayingRecording, setReplayingRecording] = useState<T | null>(null);

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    async function loadRecordings() {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });

      if (connectionMethod) params.set("connectionMethod", connectionMethod);

      if (debouncedSearch) params.set("search", debouncedSearch);
      const from = localDayStart(dateFrom);
      if (from) params.set("from", from);
      const to = localDayEnd(dateTo);
      if (to) params.set("to", to);

      const data = await api.get<PagedResult<T>>(
        `/organizations/${orgId}/${path}?${params.toString()}`,
      );

      if (cancelled) {
        return;
      }

      if (data) {
        setRecordings(data.items);
        setTotalCount(data.totalCount);
        setError(null);
      } else {
        setError(`Couldn't load ${label} recordings.`);
      }
    }

    void loadRecordings();

    const interval = setInterval(() => void loadRecordings(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    accessToken,
    api,
    orgId,
    page,
    debouncedSearch,
    dateFrom,
    dateTo,
    path,
    connectionMethod,
    label,
  ]);

  useEffect(() => {
    if (!recordings) {
      return;
    }

    setSummary({ total: totalCount, noun: "recording" });

    return () => setSummary(null);
  }, [recordings, totalCount, setSummary]);

  const hasFilters = Boolean(debouncedSearch || dateFrom || dateTo);

  function clearFilters() {
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  }

  const isEmpty = recordings !== null && totalCount === 0;

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <SearchField
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label={`Search ${label} recordings`}
          placeholder={searchPlaceholder}
        />
        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={(newFrom, newTo) => {
            setDateFrom(newFrom);
            setDateTo(newTo);
            setPage(1);
          }}
        />
      </div>

      {error && !recordings && (
        <LoadError
          what={`${label} recordings`}
          onRetry={() => setPage(1)}
          className="mb-4"
        />
      )}

      {isEmpty ? (
        <EmptyState
          title={
            hasFilters
              ? `No ${label} recordings match these filters`
              : `No ${label} recordings yet`
          }
          description={
            hasFilters
              ? "Try a different name or date range."
              : (emptyDescription ??
                `Recorded sessions will appear here after people connect over ${label}.`)
          }
        >
          {hasFilters && <Button onClick={clearFilters}>Clear filters</Button>}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <thead>
              <tr>
                {[
                  "Resource",
                  "User",
                  "Started",
                  "Duration",
                  ...(extraColumn ? [extraColumn.label] : []),
                  ...(showSize ? ["Size"] : []),
                ].map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!recordings ? (
                <SkeletonRows
                  columns={2 + (extraColumn ? 1 : 0) + (showSize ? 1 : 0)}
                />
              ) : (
                recordings.map((recording) => (
                  <tr
                    key={recording.id}
                    className="h-14 cursor-pointer border-b border-surface/70 hover:bg-surface"
                    onClick={() => setReplayingRecording(recording)}
                  >
                    <td className="py-2 pr-4">{recording.resourceName}</td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2.5">
                        <Avatar
                          name={recording.requestedByUserName}
                          email={recording.requestedByUserEmail}
                          avatarId={recording.requestedByUserAvatarId}
                          size="md"
                        />
                        <span className="truncate text-foreground/70">
                          {recording.requestedByUserName ??
                            recording.requestedByUserEmail ??
                            "Unknown"}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap py-2 pr-4 tabular-nums text-foreground/70">
                      {formatDateTime(recording.requestedAt)}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {formatDuration(recording.durationSeconds)}
                    </td>
                    {extraColumn && (
                      <td className="whitespace-nowrap py-2 pr-4 tabular-nums text-foreground/70">
                        {extraColumn.render(recording)}
                      </td>
                    )}
                    {showSize && (
                      <td className="py-2 pr-4 tabular-nums text-foreground/70">
                        {formatSize(recording.sizeBytes ?? 0)}
                        {recording.truncated && (
                          <>
                            {" "}
                            <Chip variant="warning">Incomplete recording</Chip>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!isEmpty && (
        <Pagination
          page={page}
          totalPages={Math.ceil(totalCount / PAGE_SIZE)}
          onPageChange={setPage}
        />
      )}

      {replayingRecording &&
        renderModal(replayingRecording, () => setReplayingRecording(null))}
    </div>
  );
}
